import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../styles/AdminDashboard.css";

function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [author, setAuthor] = useState("Biko");
  const [conclusion, setConclusion] = useState("");
  const [sections, setSections] = useState([{ heading: "", body: "" }]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(false);

  useEffect(() => {
    if (!editId) return;
    setLoadingBlog(true);
    fetch(`http://localhost:5000/api/blogs/${editId}`)
      .then(res => res.json())
      .then(data => {
        setTitle(data.title || "");
        setSummary(data.summary || "");
        setAuthor(data.author || "Biko");
        setConclusion(data.conclusion || "");
        setSections(data.sections?.length ? data.sections : [{ heading: "", body: "" }]);
        setExistingImage(data.image || "");
        setLoadingBlog(false);
      })
      .catch(() => setLoadingBlog(false));
  }, [editId]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async () => {
    if (!imageFile) return existingImage;
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("image", imageFile);

    const res = await fetch("http://localhost:5000/api/upload", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });

    const data = await res.json();
    return data.url || "";
  };

  const addSection = () =>
    setSections([...sections, { heading: "", body: "" }]);

  const removeSection = (index) =>
    setSections(sections.filter((_, i) => i !== index));

  const updateSection = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/admin/login"); return; }
    if (!title || !summary || !conclusion) {
      alert("Title, summary and conclusion are required");
      return;
    }

    setLoading(true);
    try {
      const imageUrl = await uploadImage();
      const payload = { title, summary, author, image: imageUrl, sections, conclusion };

      const url = editId
        ? `http://localhost:5000/api/blogs/${editId}`
        : "http://localhost:5000/api/blogs";
      const method = editId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) { alert("Failed to save blog"); return; }

      alert(editId ? "Blog updated ✅" : "Blog published 🚀");
      navigate("/blogs");
    } catch (err) {
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingBlog) return (
    <div className="admin-dashboard">
      <div className="admin-loading">Loading blog...</div>
    </div>
  );

  return (
    <div className="admin-dashboard">
      <div className="admin-panel">
        <h2>{editId ? "Edit Blog" : "Create Blog"}</h2>

        <label>Author</label>
        <input
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Your name"
        />

        <label>Title</label>
        <input
          placeholder="Blog title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label>Summary</label>
        <input
          placeholder="Short summary shown on card"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />

        <label>Cover Image</label>
        <div className="image-upload-wrapper">
          <label className="image-upload-btn" htmlFor="image-input">
            {imagePreview || existingImage ? "Change Image" : "Upload Image"}
          </label>
          <input
            id="image-input"
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
          {(imagePreview || existingImage) && (
            <div className="image-preview">
              <img src={imagePreview || existingImage} alt="Preview" />
              <button
                className="remove-image-btn"
                onClick={() => {
                  setImageFile(null);
                  setImagePreview("");
                  setExistingImage("");
                }}
              >
                ✕ Remove
              </button>
            </div>
          )}
          <p className="image-hint">JPG, JPEG or PNG — max 5MB</p>
        </div>

        <div className="sections-wrapper">
          <div className="sections-header">
            <h3>Sections</h3>
            <button className="add-section-btn" onClick={addSection}>
              + Add Section
            </button>
          </div>

          {sections.map((section, i) => (
            <div key={i} className="section-block">
              <div className="section-block-header">
                <span>Section {i + 1}</span>
                {sections.length > 1 && (
                  <button className="remove-btn" onClick={() => removeSection(i)}>
                    Remove
                  </button>
                )}
              </div>
              <input
                placeholder={`Heading ${i + 1}`}
                value={section.heading}
                onChange={(e) => updateSection(i, "heading", e.target.value)}
              />
              <textarea
                placeholder="Section body..."
                value={section.body}
                onChange={(e) => updateSection(i, "body", e.target.value)}
              />
            </div>
          ))}
        </div>

        <label>Conclusion</label>
        <textarea
          placeholder="Conclusion paragraph..."
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
        />

        <div className="admin-actions">
          <button className="cancel-btn" onClick={() => navigate("/blogs")}>
            Cancel
          </button>
          <button className="publish-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : editId ? "Update Blog ✅" : "Publish Blog 🚀"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;